import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
// Import directly (not via the ../notifications barrel) so we don't pull in
// NotificationList, which statically imports the aws-amplify-backed lib. Keeps
// the logged-out boot path free of the SDK.
import { NotificationBadge } from '../notifications/NotificationBadge';
import { useModeToggle } from '../../hooks/useModeToggle';
import { useHasManageAccess } from '../../hooks/useQueries';

// =============================================================================
// Logged-out primary nav
// =============================================================================

export interface PublicNavLink {
  to: string;
  /** Label from `sm` up. */
  label: string;
  /** Shorter label below `sm`, where three full phrases won't fit beside Sign In. */
  shortLabel: string;
  /**
   * Dropped entirely below `sm`. The 120px logo, two nav links and the Sign In
   * button already fill a 375px viewport; a third link overflows it.
   */
  hiddenBelowSm?: boolean;
}

/**
 * The three things a visitor can do without an account, promoted out of the
 * landing page's footer CTAs into persistent chrome. RootLayout only renders
 * BottomNav for authenticated users, so before this the header — a logo and a
 * Sign In button — was the entire navigation available to a logged-out
 * visitor, and every one of these was reachable only by scrolling the home
 * page to the bottom.
 *
 * "Start a club" goes via sign-in because requesting one needs an account;
 * the redirect lands them on the request form straight after.
 */
export const PUBLIC_NAV_LINKS: readonly PublicNavLink[] = [
  { to: '/clubs', label: 'Find a club', shortLabel: 'Clubs' },
  { to: '/play', label: 'Score a battle', shortLabel: 'Play' },
  {
    to: '/sign-in?redirect=%2Fclubs%2Frequest',
    label: 'Start a club',
    shortLabel: 'Start a club',
    hiddenBelowSm: true,
  },
];

/**
 * Application header: logo + mode badge + notification bell + profile, or the
 * public nav + Sign In when logged out.
 * Sign-out lives on the Profile page (it doesn't belong as persistent chrome).
 */
/**
 * Where the profile icon sends you.
 *
 * Closing returns you to what you were doing rather than to a fixed
 * destination: "where I was" is almost always a tournament in progress, and
 * sending someone home would be a second navigation to undo. The history index
 * covers the case with nothing behind — /profile opened directly, or a fresh
 * install — where going back would leave the app entirely.
 */
export function profileTapTarget(input: {
  isOnProfile: boolean;
  historyIndex: number;
}): 'open-profile' | 'go-back' | 'go-home' {
  if (!input.isOnProfile) return 'open-profile';
  return input.historyIndex > 0 ? 'go-back' : 'go-home';
}

export function Header() {
  const { isAuthenticated, isLoading } = useAuth();
  const { isTdMode, toggleMode } = useModeToggle();
  const location = useLocation();
  // Only club OWNER/TD accounts can reach TD mode at all, so the badge stays
  // out of the way for the vast majority of users who are just players.
  const { hasManageAccess } = useHasManageAccess();
  const showModeBadge = isAuthenticated && hasManageAccess;
  // Carry the current location through sign-in so a spectator tapping Sign
  // In from a tournament page returns there, instead of landing on Home.
  const signInHref = `/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`;

  const navigate = useNavigate();
  const isOnProfile = location.pathname === '/profile';

  const handleProfileTap = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    switch (profileTapTarget({ isOnProfile, historyIndex: idx })) {
      case 'open-profile':
        navigate('/profile');
        break;
      case 'go-back':
        navigate(-1);
        break;
      case 'go-home':
        navigate('/');
        break;
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-bbx-dark-800/95 backdrop-blur-sm border-b-2 safe-area-inset-top transition-colors ${
        isTdMode ? 'border-b-bbx-warning' : 'border-b-bbx-dark-700'
      }`}
    >
      <div className="flex items-center justify-between h-14 px-4">
        {/* Logo / App name */}
        <Link
          to="/"
          className="flex items-center h-10 hover:opacity-80 transition-opacity"
          aria-label="BBXT Home"
        >
          <img
            src="/logo-header.png"
            alt="BBXT"
            className="h-10 w-auto"
          />
        </Link>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {!isLoading && isAuthenticated && (
            <>
              {showModeBadge && <ModeBadge isTdMode={isTdMode} onToggle={toggleMode} />}

              {/* Notification bell with dropdown */}
              <NotificationBadge />

              {/* Profile toggle. A plain link left you stranded: tapping the icon
                  again re-navigated to the page you were already on, so the only
                  way back was the browser control. It now behaves the way the
                  icon implies — tap to open, tap to close. */}
              <button
                type="button"
                onClick={handleProfileTap}
                className="p-2 min-w-tap-min min-h-tap-min flex items-center justify-center rounded-full hover:bg-bbx-dark-700 transition-colors"
                aria-label={isOnProfile ? 'Close profile' : 'Profile'}
                aria-pressed={isOnProfile}
              >
                <svg
                  className="w-6 h-6 text-bbx-dark-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </button>
            </>
          )}

          {!isLoading && !isAuthenticated && (
            <>
              <nav aria-label="Primary" className="flex items-center">
                {PUBLIC_NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-2 sm:px-3 py-2 min-h-tap-min items-center text-sm font-medium text-bbx-dark-300 hover:text-white transition-colors whitespace-nowrap ${
                      link.hiddenBelowSm ? 'hidden sm:flex' : 'flex'
                    }`}
                  >
                    <span className="sm:hidden">{link.shortLabel}</span>
                    <span className="hidden sm:inline">{link.label}</span>
                  </Link>
                ))}
              </nav>

              <Link
                to={signInHref}
                className="px-3 sm:px-4 py-2 bg-bbx-primary-600 hover:bg-bbx-primary-500 text-white text-sm font-medium rounded-lg transition-colors min-h-tap-min flex items-center whitespace-nowrap"
              >
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// =============================================================================
// Mode badge
// =============================================================================

interface ModeBadgeProps {
  isTdMode: boolean;
  onToggle: () => void;
}

/**
 * Tap target that both shows the current mode (colour + label, derived from
 * the route — see useModeToggle) and switches to the other one, returning to
 * wherever the user last was there.
 */
function ModeBadge({ isTdMode, onToggle }: ModeBadgeProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={isTdMode ? 'Switch to Player mode' : 'Switch to TD mode'}
      className={`flex items-center gap-1.5 min-h-tap-min pl-3 pr-2.5 py-1.5 rounded-full border text-sm font-medium transition-colors ${
        isTdMode
          ? 'border-bbx-warning/50 bg-bbx-warning/10 text-bbx-warning hover:bg-bbx-warning/20'
          : 'border-bbx-dark-600 bg-bbx-dark-700 text-bbx-dark-200 hover:bg-bbx-dark-600'
      }`}
    >
      <span>{isTdMode ? 'TD Mode' : 'Player'}</span>
      <SwapIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
    </button>
  );
}

function SwapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4"
      />
    </svg>
  );
}
